import React, { Fragment, useCallback, useEffect, useReducer } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import moment from "moment-timezone";
import { Datepicker, momentTimezone } from "@mobiscroll/react";

import ai from "../../helpers/axios";
import PageHeaderRowSearch from "../shared/page-header-row-search";
import Loader from "../loader";
import SwitchInput from "../reusable-modules/switch-input";

const reducer = (state, action) => {
  switch (action.type) {
    case "dataBeganDownloading": {
      return {
        ...state,
        isLoading: true,
      };
    }

    case "dataDownloaded": {
      return {
        ...state,
        isLoading: false,
        data: action?.payload?.data || [],
      };
    }

    case "rowUpdated": {
      return {
        ...state,
        data: state.data.map((item) => item.user_id === action?.payload?.data.user_id ? action.payload.data : item),
      };
    }

    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    default:
      break;
  }
  return state;
};

const initialState = {
  isLoading: true,
  isSaving: false,
  data: [],
};

const minDate = moment(new Date()).startOf("hour").add(1, "hour").utc().format("YYYY-MM-DD[T]hh:mmZ");

const DutyStatusManagementContent = () => {
  const history = useHistory();
  const { user } = useAuth0();

  const userRoles = user && user["https://new.evojets.com/roles"];
  const superAdmin =
    user &&
    userRoles.some(function (role) {
      return "Super Admin" === role;
    });

  const isDutyAdmin = user && user["https://new.evojets.com/appMetadata"]?.isDutyAdmin;

  useEffect(() => {
    if (!superAdmin && !isDutyAdmin) {
      toast.error("You do not have permission to view this page.");
      history.push("/");
    }
  }, [superAdmin, isDutyAdmin, history]);

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialState);

  const getData = useCallback(
    async () => {
      const token = await getAccessTokenSilently().catch(loginWithRedirect);

      const { data } = await ai.auth(token).get(`/api/users/dutySales`, {});

      dispatch({
        type: "dataDownloaded",
        payload: {
          data,
        },
      });
    },
    [getAccessTokenSilently, loginWithRedirect],
  );

  useEffect(() => {
    document.title = "Duty Status Management | evoJets HQ";
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleToggleOnDuty = useCallback(async (user_id, isOnDuty) => {
    if (!isOnDuty && !state.data.filter((user) => user.isOnDuty && user.user_id !== user_id).length) {
      return toast.error("There must be at least one user on duty.");
    }

    if (state.isSaving) {
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    const token = await getAccessTokenSilently().catch(loginWithRedirect);

    try {
      const response = await ai
        .auth(token)
        .patch(`/api/users/dutySales/${user_id}`, { isOnDuty });

      dispatch({
        type: "rowUpdated",
        payload: {
          data: response.data,
        },
      });

      toast.success("Duty status updated");

      if (user_id === user.sub) {
        await getAccessTokenSilently({
          ignoreCache: true,
        });
      }
    } catch (error) {
      return toast.error("There must be at least one user on duty.");
    } finally {
      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
    }
  }, [getAccessTokenSilently, loginWithRedirect, state.data, user, state.isSaving]);

  const handleChangeOffDutyUntil = useCallback(async (user_id, offDutyUntil) => {
    if (!offDutyUntil) {
      return toast.error("Off Duty Until date cannot be empty");
    }

    if (moment(offDutyUntil).isBefore(moment())) {
      return toast.error("Off Duty Until date cannot be in the past");
    }

    const token = await getAccessTokenSilently().catch(loginWithRedirect);

    const response = await ai
      .auth(token)
      .patch(`/api/users/dutySales/${user_id}`, { offDutyUntil })
      .catch((error) => console.error(error.response.data));

    if (response?.data) {
      dispatch({
        type: "rowUpdated",
        payload: {
          data: response.data,
        },
      });
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  return (
    <Fragment>
      <PageHeaderRowSearch
        h1="Duty Status Management"
        breadcrumbs={[{ link: "/pending-payments", label: "Duty Status Management" }]}
        extraClasses="page-header-two-columns-l1"
      />

      <div className="duty-status-table mt-20">
        <table className="w-100">
          <thead className="fm-l2-parent-table-head dynamic-accordion-table-header">
          <tr className="fm-l2-parent-table-head-row">
            <th className="text-align-left">Sales Rep</th>
            <th className="text-align-left">Duty Status</th>
            <th className="text-align-left">Off Duty Until</th>
          </tr>
          </thead>
          <tbody className="fm-l2-parent-table-body">
          {state.isLoading ? (
              <tr className="fm-l2-parent-table-row">
                <td colSpan={3}>
                  <div className="position-absolute w-100 h-100 custom-loader-wrapper">
                    <Loader additionalClasses="position-absolute top-50 start-50 translate-middle"/>
                  </div>
                </td>
              </tr>
            )
            : !state.data.length ? (
                <tr className="fm-l2-parent-table-row">
                  <td colSpan={3}>
                    No Duty Sales assigned yet
                  </td>
                </tr>
              )
              : state.data.map(({ user_id, name, isOnDuty, offDutyUntil }) => (
                <tr key={user_id} className="fm-l2-parent-table-row">
                  <td>
                    {name}
                  </td>
                  <td>
                    <SwitchInput
                      checked={isOnDuty}
                      onChange={(event) => handleToggleOnDuty(user_id, event.target.checked)}
                      leftLabel={
                        <>
                          {isOnDuty ? <span className="label-green">On Duty</span> : null}
                        </>
                      }
                      rightLabel={
                        <>
                          {!isOnDuty ? <span className="label-red">Off Duty</span> : null}
                        </>
                      }
                      containerClasses="duty-switch-container"
                    />
                  </td>
                  <td>
                    <Datepicker
                      value={offDutyUntil}
                      onChange={(event) => {
                        handleChangeOffDutyUntil(user_id, event.value);
                      }}
                      dataTimezone="utc"
                      displayTimezone="local"
                      timezonePlugin={momentTimezone}
                      controls={["date", "time"]}
                      min={minDate}
                      disabled={isOnDuty}
                      stepMinute={60}
                    />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </Fragment>
  );
};

export default DutyStatusManagementContent;
